import { useState } from "react"
import { ControlledModal, Dialog, DialogContent } from "../Dialog"
import { DialogHeader } from "../Dialog/DialogHeader"
import { Text } from "@/elements/Text"
import { TextArea } from "@/elements/TextArea"
import { Button, Separator } from "@/elements"
import { DialogSelectItem } from "../Dialog/DialogSelectItem"
import { UserReportReason } from "@/gql/graphql"
import { useMutation } from "@apollo/client"
import { graphql } from "@/gql"
import { toast } from "@/components/Toast"
import { captureException } from "@sentry/nextjs"

const reportUserMutation = graphql(`
  mutation ReportUser($input: CreateUserReportInput!) {
    createUserReport(input: $input) {
      userReport {
        __typename
        createdAt
        updatedAt
        id
        details
        reason
        user {
          id
        }
      }
      errors {
        field
        messages
      }
    }
  }
`)

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  userId: string | undefined | null
}

const selectOptions = [
  {
    value: UserReportReason.ObjectionableContent,
    display: "Profile contains objectionable content",
  },
  {
    value: UserReportReason.Impersonation,
    display: "Impersonating someone else",
  },
  {
    value: UserReportReason.MadeMeUncomfortable,
    display: "Made me uncomfortable",
  },
  {
    value: UserReportReason.Other,
    display: "Something else",
  },
]

const StepOne: React.FC<{
  selectedOption: string
  onSelectOption: (value: UserReportReason) => void
}> = ({ selectedOption, onSelectOption }) => {
  return (
    <div>
      <Text variant="h6">What&apos;s going on?</Text>
      <div className="my-5">
        <Text className="text-fg-3" variant="body1">
          Help us keep the NOVA community safe by telling us why you’d like to report this user.
        </Text>
      </div>
      <Separator />
      {selectOptions.map((option, index) => {
        const isSelected = option.value === selectedOption
        return (
          <div key={option.value}>
            <DialogSelectItem
              onClick={() => onSelectOption(option.value)}
              label={option.display}
              isSelected={isSelected}
            />
            <Separator />
          </div>
        )
      })}
    </div>
  )
}

const StepTwo: React.FC<{
  detailText: string
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
}> = ({ detailText, onChange }) => {
  return (
    <div className="h-full w-full">
      <Text variant="h6">Provide more detail (optional)</Text>
      <TextArea value={detailText} onChange={onChange} className="mt-5 h-full w-full" placeholder="Add detail here" />
    </div>
  )
}

export const ReportUserDialog: React.FC<Props> = ({ open, userId, ...props }) => {
  const [step, setStep] = useState(1)
  const [selectedOption, setSelectedOption] = useState(UserReportReason.ObjectionableContent)
  const [detailText, setDetailText] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [reportUser] = useMutation(reportUserMutation)

  const onOpenChange = (open: boolean) => {
    if (!open) {
      setStep(1)
      setDetailText("")
      setSelectedOption(UserReportReason.ObjectionableContent)
    }
    props.onOpenChange(open)
  }

  const submit = () => {
    setIsLoading(true)
    reportUser({
      variables: {
        input: {
          reason: selectedOption,
          details: detailText,
          user: userId!,
        },
      },
      onCompleted(data) {
        if (data.createUserReport?.errors.length !== 0) {
          toast.success("Error reporting user")
          throw new Error(data.createUserReport?.errors.toString())
        } else {
          toast.success("User reported")
          onOpenChange(false)
        }
        setIsLoading(false)
      },
      onError(error) {
        captureException(error)

        setIsLoading(false)
        toast.error("Error reporting user")
      },
    })
  }

  const handleButton = () => {
    if (step === 1) {
      setStep(2)
    } else {
      submit()
    }
  }

  const onSelectOption = (value: UserReportReason) => {
    setSelectedOption(value)
  }

  return (
    <ControlledModal
      open={open}
      onOpenChange={onOpenChange}
      header={{ title: "", variant: "default", fixed: true }}
      body={
        <div className="h-full w-full">
          {step === 1 ? (
            <StepOne selectedOption={selectedOption} onSelectOption={onSelectOption} />
          ) : (
            <StepTwo detailText={detailText} onChange={e => setDetailText(e.target.value)} />
          )}
        </div>
      }
      footer={{
        primaryButton: {
          children: step === 1 ? "Next" : "Submit",
          onClick: handleButton,
          loading: isLoading,
        },
        fixed: true,
      }}
    />
  )
}
